import { createContext, type ReactNode, useContext } from 'react';

interface CachedImage {
  url: string;
  objectUrl?: string;
  timestamp: number;
  loading: boolean;
  error: boolean;
}

interface ImageCacheContextValue {
  getCachedImage: (url: string) => CachedImage | null;
  cacheImage: (url: string) => Promise<string>;
}

const ImageCacheContext = createContext<ImageCacheContextValue | null>(null);

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
const MEMORY_CACHE = new Map<string, CachedImage>();

export function ImageCacheProvider({ children }: { children: ReactNode }) {
  const getCachedImage = (url: string): CachedImage | null => {
    // Check memory cache first
    const memoryImage = MEMORY_CACHE.get(url);
    if (memoryImage && !isExpired(memoryImage.timestamp)) {
      return memoryImage;
    }

    // Check localStorage cache
    try {
      const stored = localStorage.getItem(`img_cache_${url}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (!isExpired(parsed.timestamp)) {
          const cachedImage: CachedImage = {
            url,
            objectUrl: parsed.objectUrl,
            timestamp: parsed.timestamp,
            loading: false,
            error: false,
          };
          MEMORY_CACHE.set(url, cachedImage);
          return cachedImage;
        } else {
          // Expired, remove from localStorage
          localStorage.removeItem(`img_cache_${url}`);
        }
      }
    } catch (error) {
      console.warn('Error reading from image cache:', error);
    }

    return null;
  };

  const cacheImage = async (url: string): Promise<string> => {
    // Check if already cached and valid
    const existing = getCachedImage(url);
    if (existing?.objectUrl && !existing.loading && !existing.error) {
      return existing.objectUrl;
    }

    // Mark as loading
    const loadingImage: CachedImage = {
      url,
      timestamp: Date.now(),
      loading: true,
      error: false,
    };
    MEMORY_CACHE.set(url, loadingImage);

    try {
      // Fetch the image
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const cachedImage: CachedImage = {
        url,
        objectUrl,
        timestamp: Date.now(),
        loading: false,
        error: false,
      };

      // Store in memory cache
      MEMORY_CACHE.set(url, cachedImage);

      // Store in localStorage (without the blob, just metadata)
      try {
        localStorage.setItem(
          `img_cache_${url}`,
          JSON.stringify({
            timestamp: cachedImage.timestamp,
            objectUrl: objectUrl,
          }),
        );
      } catch (error) {
        console.warn('Failed to store image in localStorage:', error);
      }

      return objectUrl;
    } catch (error) {
      console.error('Error caching image:', error);

      // Mark as error
      const errorImage: CachedImage = {
        url,
        timestamp: Date.now(),
        loading: false,
        error: true,
      };
      MEMORY_CACHE.set(url, errorImage);

      throw error;
    }
  };

  return <ImageCacheContext.Provider value={{ getCachedImage, cacheImage }}>{children}</ImageCacheContext.Provider>;
}

export function useImageCache() {
  const context = useContext(ImageCacheContext);
  if (!context) {
    throw new Error('useImageCache must be used within an ImageCacheProvider');
  }
  return context;
}

function isExpired(timestamp: number): boolean {
  return Date.now() - timestamp > CACHE_DURATION;
}

// Cleanup function to remove expired items and revoke object URLs
export function cleanupImageCache() {
  try {
    // Clean memory cache
    for (const [url, image] of MEMORY_CACHE.entries()) {
      if (isExpired(image.timestamp)) {
        if (image.objectUrl) {
          URL.revokeObjectURL(image.objectUrl);
        }
        MEMORY_CACHE.delete(url);
      }
    }

    // Clean localStorage
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.startsWith('img_cache_')) {
        try {
          const stored = JSON.parse(localStorage.getItem(key) || '');
          if (isExpired(stored.timestamp)) {
            localStorage.removeItem(key);
          }
        } catch {
          // Invalid data, remove it
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.warn('Error during image cache cleanup:', error);
  }
}

// Run cleanup when the page loads
if (typeof window !== 'undefined') {
  cleanupImageCache();
}
